import React, { useState, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import {
  X,
  Mail,
  ArrowRight,
  Trash2,
  Users
} from 'lucide-react';
import SendEmailModal from '../SendEmailModal';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const BulkActionsPanel = ({ selectedProspects, processData, onClearSelection, onRefresh }) => {
  const API = useContext(ApiContext);
  const [loading, setLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [individualIdsForEmail, setIndividualIdsForEmail] = useState([]);
  const [selectedNextStep, setSelectedNextStep] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!processData || selectedProspects.length === 0) {
    return null;
  }

  const activeSteps = processData.steps.filter(step => step.active).sort((a, b) => a.order - b.order);

  const handleOpenEmailModal = async () => {
    setLoading(true);
    try {
      // Récupérer tous les prospects du processus
      const allProspects = processData.prospects || [];

      // Filtrer les prospects sélectionnés et extraire leurs individualIds
      const selectedProspectObjects = allProspects.filter(prospect =>
        selectedProspects.includes(prospect._id)
      );

      // Extraire les individual IDs
      const individualIds = selectedProspectObjects.map(prospect => prospect.individual._id);

      setIndividualIdsForEmail(individualIds);
      setShowEmailModal(true);
    } catch (error) {
      console.error('Error fetching prospect data:', error);
      toast.error('Erreur lors de la récupération des données des prospects');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToNextStep = async () => {
    if (!selectedNextStep) {
      toast.error('Veuillez sélectionner une étape');
      return;
    }

    setLoading(true);
    try {
      const response = await API.post(`/api/process-bizdev/${processData._id}/bulk-actions/move-step`, {
        prospectIds: selectedProspects,
        newStep: selectedNextStep
      });

      toast.success(`${selectedProspects.length} prospect(s) déplacé(s) vers ${selectedNextStep}`);
      onClearSelection();
      onRefresh();
    } catch (error) {
      console.error('Error moving prospects:', error);
      toast.error('Erreur lors du déplacement des prospects');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromProcess = async () => {
    setShowDeleteDialog(false);

    setLoading(true);
    try {
      const response = await API.post(`/api/process-bizdev/${processData._id}/bulk-actions/remove`, {
        prospectIds: selectedProspects
      });

      toast.success(`${selectedProspects.length} prospect(s) retiré(s) du processus`);
      onClearSelection();
      onRefresh();
    } catch (error) {
      console.error('Error removing prospects:', error);
      toast.error('Erreur lors de la suppression des prospects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Panneau d'actions multiples */}
      <div className="rounded-lg mb-2 bg-slate-800 text-white px-6 py-4 shadow-lg border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Users size={20} />
              <span className="font-medium">
                {selectedProspects.length} prospect{selectedProspects.length > 1 ? 's' : ''} sélectionné{selectedProspects.length > 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="h-4 w-px bg-blue-300"></div>
            
            <div className="flex items-center space-x-2">
              {/* Bouton Email */}
              <button
                onClick={handleOpenEmailModal}
                disabled={loading}
                className="flex items-center space-x-2 bg-indigo-500 hover:bg-indigo-400 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                <Mail size={16} />
                <span>Envoyer email</span>
              </button>

              {/* Sélecteur d'étape suivante */}
              <div className="flex items-center space-x-2">
                <select
                  value={selectedNextStep}
                  onChange={(e) => setSelectedNextStep(e.target.value)}
                  className="bg-white border  text-indigo-500 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <option value="">Déplacer vers...</option>
                  {activeSteps.map((step) => (
                    <option key={step.name} value={step.name} className="text-gray-900">
                      {step.name}
                    </option>
                  ))}
                </select>
                
                <button
                  onClick={handleMoveToNextStep}
                  disabled={loading || !selectedNextStep}
                  className="flex text-indigo-100 items-center space-x-1 bg-slate-600 hover:bg-slate-500 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  <ArrowRight size={16} />
                  <span>Déplacer</span>
                </button>
              </div>

              {/* Bouton Supprimer */}
              <button
                onClick={() => setShowDeleteDialog(true)}
                disabled={loading}
                className="flex items-center space-x-2 bg-red-500 hover:bg-red-400 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 size={16} />
                <span>Retirer</span>
              </button>
            </div>
          </div>

          <button
            onClick={onClearSelection}
            className="flex items-center space-x-1 text-blue-200 hover:text-white transition-colors"
          >
            <X size={20} />
            <span>Annuler</span>
          </button>
        </div>

        {loading && (
          <div className="mt-3 flex items-center space-x-2 text-blue-200">
            <div className="w-4 h-4 border-2 border-blue-300 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm">Action en cours...</span>
          </div>
        )}
      </div>

      {/* Modal d'envoi d'email */}
      {showEmailModal && (
        <SendEmailModal
          recipients={individualIdsForEmail}
          onClose={() => setShowEmailModal(false)}
          onSend={() => {
            setShowEmailModal(false);
            onClearSelection();
          }}
        />
      )}

      {/* Confirmation dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleRemoveFromProcess}
        title="Retirer les prospects"
        message={`Êtes-vous sûr de vouloir retirer ${selectedProspects.length} prospect(s) de ce processus ?`}
        confirmText="Retirer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </>
  );
};

export default BulkActionsPanel;